import { useCallback, useMemo } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Coach } from '@court-sync/shared';
import { coachService } from '../services/coach';
import { STALE_TIME, queryClient } from '../queryClient';
import { queryKeys } from './keys';
import { unwrapApiData, unwrapApiDataOrEmpty } from './unwrap';
import { STABLE_EMPTY_COACHES } from './emptyDefaults';

export function useCoaches(academyId?: string) {
  const queryClient = useQueryClient();
  const queryKey = queryKeys.coaches.list(academyId ?? '');

  const { data: coaches = STABLE_EMPTY_COACHES, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await coachService.getCoaches(academyId);
      return unwrapApiDataOrEmpty(response);
    },
    enabled: !!academyId,
    staleTime: STALE_TIME.MEDIUM,
  });

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.coaches.lists() });
  }, [queryClient]);

  const createMutation = useMutation({
    mutationFn: async (data: Omit<Coach, 'id' | 'createdAt' | 'updatedAt'>) => {
      const response = await coachService.createCoach(data);
      const id = unwrapApiData(response, 'Failed to create coach').id;
      const detail = await coachService.getCoach(id);
      return unwrapApiData(detail, 'Failed to fetch created coach');
    },
    onSuccess: invalidate,
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<Omit<Coach, 'id' | 'createdAt' | 'updatedAt'>>;
    }) => {
      await coachService.updateCoach(id, data);
      const detail = await coachService.getCoach(id);
      return unwrapApiData(detail, 'Failed to fetch updated coach');
    },
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<Coach[]>(queryKey);
      queryClient.setQueryData<Coach[]>(queryKey, (old) =>
        old?.map((coach) => (coach.id === id ? { ...coach, ...data, updatedAt: new Date() } : coach))
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
    },
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await coachService.deleteCoach(id);
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<Coach[]>(queryKey);
      queryClient.setQueryData<Coach[]>(queryKey, (old) => old?.filter((c) => c.id !== id));
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
    },
    onSuccess: invalidate,
  });

  const getCoachById = useCallback(
    (id: string) => coaches.find((c) => c.id === id),
    [coaches]
  );

  const refresh = useCallback(() => refetch(), [refetch]);

  return {
    coaches,
    isLoading,
    error: error instanceof Error ? error.message : error ? String(error) : null,
    refresh,
    createCoach: createMutation.mutateAsync,
    updateCoach: (id: string, data: Parameters<typeof updateMutation.mutateAsync>[0]['data']) =>
      updateMutation.mutateAsync({ id, data }),
    deleteCoach: deleteMutation.mutateAsync,
    getCoachById,
  };
}

/** Read coach from any cached list (for schedule components). */
export function getCoachFromCache(id: string): Coach | undefined {
  const entries = queryClient.getQueriesData<Coach[]>({ queryKey: queryKeys.coaches.lists() });
  for (const [, coaches] of entries) {
    const match = coaches?.find((c) => c.id === id);
    if (match) return match;
  }
  return undefined;
}

/** Fetch a single coach and merge into list caches (schedule fallback). */
export async function fetchCoachQuery(id: string): Promise<Coach | null> {
  try {
    const response = await coachService.getCoach(id);
    const coach = unwrapApiData(response, 'Failed to fetch coach');
    for (const academyId of coach.academyIds) {
      const listKey = queryKeys.coaches.list(academyId);
      queryClient.setQueryData<Coach[]>(listKey, (old) => {
        if (!old) return [coach];
        if (old.some((c) => c.id === id)) {
          return old.map((c) => (c.id === id ? coach : c));
        }
        return [...old, coach];
      });
    }
    return coach;
  } catch {
    return null;
  }
}

export type CoachInviteStatus = 'pending' | 'accepted' | 'expired' | 'revoked';

export interface CoachStats {
  attendanceRate?: number;
  totalLessons?: number;
  reportedAbsences?: number;
}

/** Invite status map for all coaches in an academy (single request). */
export function useCoachInviteStatus(academyId?: string, options?: { enabled?: boolean }) {
  const queryClient = useQueryClient();
  const enabled = options?.enabled ?? true;
  const queryKey = queryKeys.coaches.inviteStatus(academyId ?? '');

  const { data: inviteStatusByCoach = {}, isLoading, refetch } = useQuery({
    queryKey,
    queryFn: () => coachService.getInviteStatus(academyId!),
    enabled: !!academyId && enabled,
    staleTime: STALE_TIME.MEDIUM,
  });

  const setInviteStatus = useCallback(
    (coachId: string, status: CoachInviteStatus) => {
      queryClient.setQueryData<Record<string, CoachInviteStatus>>(queryKey, (old) => ({
        ...(old ?? {}),
        [coachId]: status,
      }));
    },
    [queryClient, queryKey]
  );

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  return {
    inviteStatusByCoach,
    isLoading,
    refresh: () => refetch(),
    setInviteStatus,
    invalidate,
  };
}

/** Per-coach stats cached by React Query (dedupes concurrent/refetch noise). */
export function useCoachStatsForAcademy(academyId: string | undefined, coachIds: string[]) {
  const coachIdsKey = coachIds.join('|');
  const stableCoachIds = useMemo(
    () => (coachIdsKey ? [...coachIdsKey.split('|')].sort() : []),
    [coachIdsKey]
  );

  const results = useQueries({
    queries: stableCoachIds.map((coachId) => ({
      queryKey: queryKeys.coaches.stats(coachId, academyId ?? ''),
      queryFn: () => coachService.getCoachStats(coachId, academyId!),
      enabled: !!academyId && stableCoachIds.length > 0,
      staleTime: STALE_TIME.MEDIUM,
    })),
  });

  const coachStats = useMemo(() => {
    const map: Record<string, CoachStats> = {};
    stableCoachIds.forEach((id, index) => {
      const data = results[index]?.data;
      if (data) map[id] = data;
    });
    return map;
  }, [stableCoachIds, results]);

  const loadingStats = useMemo(() => {
    const map: Record<string, boolean> = {};
    stableCoachIds.forEach((id, index) => {
      map[id] = results[index]?.isLoading || false;
    });
    return map;
  }, [stableCoachIds, results]);

  return { coachStats, loadingStats };
}
